import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { APP_CONFIG, Settings } from '../config/settings';
import { appendQuery, combine } from '../shared/utilities/route';
import { CandidateInfo } from './models/candidate-info.model';
import { CandidateData } from './models/envelope-send.model';
import { RequiredDocument } from './models/required-document.model';
import { ExistingEnvelope } from './models/existing-envelope.model';
import { TemplateFolder } from './models/template-folder.model';
import { Template } from './models/template.model';
import { PortalEnvelope } from './models/portal-envelope.model';
import { EsignPermissions } from './models/esign-permissions.model';

@Injectable({ providedIn: 'root' })
export class EsignService {
    private readonly coreApiPath: string;

    constructor(private readonly http: HttpClient, @Inject(APP_CONFIG) settings: Settings) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    getCandidateInfo(contractId: number): Observable<CandidateInfo> {
        return this.http.get<CandidateInfo>(`${this.coreApiPath}/esignature/contract/${contractId}/Details`);
    }

    getRequiredDocuments(contractId: number): Observable<RequiredDocument[]> {
        return this.http.get<RequiredDocument[]>(
            `${this.coreApiPath}/esignature/contract/${contractId}/Documents/Required`
        );
    }

    getEsignDocuments(contractId: number): Observable<RequiredDocument[]> {
        return this.http.get<RequiredDocument[]>(`${this.coreApiPath}/esignature/contract/${contractId}/Documents`);
    }

    getEsignAdHocRequirements(contractId: number): Observable<RequiredDocument[]> {
        return this.http.get<RequiredDocument[]>(`${this.coreApiPath}/esignature/contract/${contractId}/Requirements`);
    }

    getTemplateFolders(forceRefresh = false): Observable<TemplateFolder[]> {
        const baseUrl = `${this.coreApiPath}/esignature/templatefolders`;
        const url = appendQuery(baseUrl, `forceRefresh=${forceRefresh}`);
        return this.http.get<TemplateFolder[]>(url);
    }

    searchTemplates(folderId: string, searchText: string): Observable<Template[]> {
        let url = `${this.coreApiPath}/esignature/templates`;
        if (folderId) {
            url = appendQuery(url, `folderId=${folderId}`);
        }
        if (searchText) {
            url = appendQuery(url, `name=${searchText}`);
        }
        return this.http.get<Template[]>(url);
    }

    associateTemplateDocumentType(
        template: Template,
        documentTypeId: number,
        contractId: number
    ): Observable<{ stateId: number; templateId: number }> {
        const url = `${this.coreApiPath}/esignature/templates/Associate`;
        return this.http.post<{ stateId: number; templateId: number }>(url, { documentTypeId, template, contractId });
    }

    associateTemplateDocumentTypeAdHoc(
        template: Template,
        documentTypeId: number,
        contractId: number
    ): Observable<{ stateId: number; templateId: number }> {
        const url = `${this.coreApiPath}/esignature/templates/associate-adhoc`;
        return this.http.post<{ stateId: number; templateId: number }>(url, { documentTypeId, template, contractId });
    }

    disassociateTemplateDocumentType(
        internalTemplateId: number,
        documentTypeId: number,
        stateId: number
    ): Observable<unknown> {
        const url = `${this.coreApiPath}/esignature/templates/Disassociate`;
        return this.http.put(url, { documentTypeId, internalTemplateId, stateId });
    }

    getTemplateImage(externalTemplateId: string): Observable<Uint8Array> {
        return this.http.get<Uint8Array>(
            `${this.coreApiPath}/esignature/templates/${externalTemplateId}/document-image`
        );
    }

    sendEnvelope(recipient: CandidateData): Observable<HttpResponse<unknown>> {
        const url = `${this.coreApiPath}/esignature/envelope/send`;
        return this.http.post<unknown>(url, recipient, { observe: 'response' });
    }

    getEnvelope(envelopeId: number): Observable<ExistingEnvelope> {
        const url = `${this.coreApiPath}/esignature/envelope/${envelopeId}`;
        return this.http.get<ExistingEnvelope>(url);
    }

    getContractEnvelopes(contractId: number): Observable<ExistingEnvelope[]> {
        const url = `${this.coreApiPath}/esignature/contract/${contractId}/envelopes`;
        return this.http.get<ExistingEnvelope[]>(url);
    }

    getPortalEnvelopes(candidateId: number): Observable<PortalEnvelope[]> {
        return this.http.get<PortalEnvelope[]>(
            `${this.coreApiPath}/esignature/portal/candidate/${candidateId}/envelopes`
        );
    }

    cancelEnvelope(envelopeId: number): Observable<unknown> {
        const url = `${this.coreApiPath}/esignature/envelope/${envelopeId}`;
        const voidEnvelope = { envelopeStatus: 8, voidedReason: 'Canceled from Nova UI' };
        return this.http.put(url, voidEnvelope);
    }

    resendEnvelope(internalEnvelopeId: number): Observable<unknown> {
        const url = `${this.coreApiPath}/esignature/envelope/${internalEnvelopeId}/resend`;
        return this.http.put(url, null);
    }

    getPermissions(): Observable<EsignPermissions> {
        const url = `${this.coreApiPath}/esignature/permissions`;
        return this.http.get<EsignPermissions>(url);
    }
}
